import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, Select, message, Popconfirm, Tabs, DatePicker, Alert, Tooltip, Divider, Statistic, Row, Col, InputNumber, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined, StopOutlined, CheckCircleOutlined, LockOutlined, UnlockOutlined, SearchOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';
import { unwrapGoNull } from '../../../shared/utils/goNull';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { TextArea } = Input;

// Entry type options
const entryTypeOptions = [
    { label: 'IP 地址', value: 'ip' },
    { label: '邮箱地址', value: 'email' },
    { label: '域名', value: 'domain' },
    { label: '发件人', value: 'sender' },
];

// Scope options
const scopeOptions = [
    { label: '全局', value: 'global' },
    { label: '域名', value: 'domain' },
    { label: '用户', value: 'user' },
];

// 快速过期时间选项
const expiryOptions = [
    { label: '永久', value: 0 },
    { label: '1 小时', value: 3600 },
    { label: '6 小时', value: 21600 },
    { label: '1 天', value: 86400 },
    { label: '7 天', value: 604800 },
    { label: '30 天', value: 2592000 },
    { label: '自定义', value: -1 },
];

const Blacklist = () => {
    const [activeTab, setActiveTab] = useState('blacklist');
    const [entries, setEntries] = useState([]);
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [blockModalVisible, setBlockModalVisible] = useState(false);
    const [unblockModalVisible, setUnblockModalVisible] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [searchValue, setSearchValue] = useState('');
    const [expiryMode, setExpiryMode] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [blockForm] = Form.useForm();
    const [unblockForm] = Form.useForm();

    // 获取集群列表
    const fetchClusters = async () => {
        try {
            const res = await adminApi.get('/webmail/clusters');
            if (res.data.code === 0) {
                const list = res.data.data.list || [];
                setClusters(list);
                const defaultCluster = list.find(c => c.is_default);
                if (defaultCluster) {
                    setSelectedCluster(defaultCluster.id);
                } else if (list.length > 0) {
                    setSelectedCluster(list[0].id);
                }
            }
        } catch (error) {
            message.error('获取集群列表失败');
        }
    };

    const fetchEntries = async (page = 1, pageSize = 20) => {
        if (!selectedCluster) return;
        setLoading(true);
        try {
            const endpoint = activeTab === 'blacklist' ? '/webmail/blacklist' : '/webmail/whitelist';
            const res = await adminApi.get(endpoint, {
                params: { cluster_id: selectedCluster, page, per_page: pageSize, search: searchValue || undefined }
            });
            if (res.data.success || res.data.code === 0) {
                const data = res.data.data || {};
                setEntries(unwrapGoNull(data.entries || data.list || []));
                setPagination({
                    current: data.page || page,
                    pageSize: data.per_page || pageSize,
                    total: data.total || (data.entries || data.list || []).length,
                });
            } else {
                message.error(res.data.error || '获取列表失败');
            }
        } catch (error) {
            message.error('获取列表失败: ' + (error.response?.data?.error || error.message));
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    useEffect(() => {
        if (selectedCluster) {
            fetchEntries();
        }
    }, [selectedCluster, activeTab]);

    const handleAdd = () => {
        form.resetFields();
        form.setFieldsValue({
            entry_type: 'ip',
            scope: 'global',
        });
        setExpiryMode(0);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const endpoint = activeTab === 'blacklist' ? `/webmail/blacklist/${id}` : `/webmail/whitelist/${id}`;
            const res = await adminApi.delete(endpoint, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.success || res.data.code === 0) {
                message.success('删除成功');
                fetchEntries(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.error || '删除失败');
            }
        } catch (error) {
            message.error('删除失败: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const endpoint = activeTab === 'blacklist' ? '/webmail/blacklist' : '/webmail/whitelist';

            let expiresAt = null;
            if (expiryMode === -1 && values.expires_at) {
                expiresAt = values.expires_at.toISOString();
            } else if (expiryMode > 0) {
                expiresAt = new Date(Date.now() + expiryMode * 1000).toISOString();
            }

            const data = {
                ...values,
                cluster_id: selectedCluster,
                expires_at: expiresAt,
            };
            delete data.expiry_preset;

            const res = await adminApi.post(endpoint, data);
            if (res.data.success || res.data.code === 0) {
                message.success('添加成功');
                setModalVisible(false);
                fetchEntries(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.error || '添加失败');
            }
        } catch (error) {
            message.error('添加失败: ' + (error.response?.data?.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    // 手动封禁
    const handleBlock = () => {
        blockForm.resetFields();
        blockForm.setFieldsValue({
            type: 'ip',
            duration: 3600,
        });
        setBlockModalVisible(true);
    };

    const handleBlockSubmit = async (values) => {
        setSubmitting(true);
        try {
            const res = await adminApi.post(`/webmail/block/${values.type}/${encodeURIComponent(values.key)}`, {
                cluster_id: selectedCluster,
                duration: values.duration,
                reason: values.reason,
            });
            if (res.data.success || res.data.code === 0) {
                message.success('封禁成功');
                setBlockModalVisible(false);
                fetchEntries(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.error || '封禁失败');
            }
        } catch (error) {
            message.error('封禁失败: ' + (error.response?.data?.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    // 解除封禁
    const handleUnblock = () => {
        unblockForm.resetFields();
        unblockForm.setFieldsValue({
            type: 'ip',
        });
        setUnblockModalVisible(true);
    };

    const handleUnblockSubmit = async (values) => {
        setSubmitting(true);
        try {
            const res = await adminApi.delete(`/webmail/block/${values.type}/${encodeURIComponent(values.key)}`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.success || res.data.code === 0) {
                message.success('解除封禁成功');
                setUnblockModalVisible(false);
            } else {
                message.error(res.data.error || '解除封禁失败');
            }
        } catch (error) {
            message.error('解除封禁失败: ' + (error.response?.data?.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSearch = () => {
        fetchEntries(1, pagination.pageSize);
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 60 },
        {
            title: '类型',
            dataIndex: 'entry_type',
            width: 90,
            render: (type) => {
                const labels = { ip: 'IP', email: '邮箱', domain: '域名', sender: '发件人' };
                const colors = { ip: 'blue', email: 'green', domain: 'purple', sender: 'orange' };
                return <Tag color={colors[type]}>{labels[type] || type}</Tag>;
            },
        },
        {
            title: '值',
            dataIndex: 'value',
            render: (text) => (
                <code style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace' }}>
                    {text}
                </code>
            ),
        },
        {
            title: '原因',
            dataIndex: 'reason',
            width: 180,
            ellipsis: true,
            render: (reason) => reason || <span style={{ color: '#999' }}>-</span>,
        },
        {
            title: '范围',
            dataIndex: 'scope',
            width: 100,
            render: (scope, record) => {
                const labels = { global: '全局', domain: '域名', user: '用户' };
                const scopeValue = record.scope_value?.String || record.scope_value;
                return (
                    <Tooltip title={scopeValue}>
                        <Tag>{labels[scope] || scope}</Tag>
                        {scopeValue && <span style={{ fontSize: 11, color: '#999' }}>{scopeValue}</span>}
                    </Tooltip>
                );
            },
        },
        {
            title: '过期时间',
            dataIndex: 'expires_at',
            width: 160,
            render: (time) => {
                if (!time) return <Tag color="red">永久</Tag>;
                const expiry = new Date(time);
                const isExpired = expiry < new Date();
                const remaining = Math.floor((expiry - new Date()) / 1000);

                if (isExpired) {
                    return <Tag color="default">已过期</Tag>;
                }

                let remainingText = '';
                if (remaining < 3600) {
                    remainingText = `${Math.floor(remaining / 60)}分钟`;
                } else if (remaining < 86400) {
                    remainingText = `${Math.floor(remaining / 3600)}小时`;
                } else {
                    remainingText = `${Math.floor(remaining / 86400)}天`;
                }

                return (
                    <Tooltip title={expiry.toLocaleString()}>
                        <Space size={4}>
                            <ClockCircleOutlined />
                            <span>{remainingText}</span>
                        </Space>
                    </Tooltip>
                );
            },
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            width: 150,
            render: (time) => time ? new Date(time).toLocaleString() : '-',
        },
        {
            title: '创建者',
            dataIndex: 'created_by',
            width: 100,
            render: (by) => by || <span style={{ color: '#999' }}>系统</span>,
        },
        {
            title: '操作',
            width: 80,
            render: (_, record) => (
                <Popconfirm title="确定删除此条目吗？" onConfirm={() => handleDelete(record.id)}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <StopOutlined />
                    黑白名单管理
                </Space>
            }
            extra={
                <Space>
                    <Select
                        style={{ width: 180 }}
                        value={selectedCluster}
                        onChange={setSelectedCluster}
                        placeholder="选择集群"
                    >
                        {clusters.map(c => (
                            <Select.Option key={c.id} value={c.id}>
                                {c.name} {c.is_default && '(默认)'}
                            </Select.Option>
                        ))}
                    </Select>
                    <Input.Search
                        placeholder="搜索 IP/邮箱/域名"
                        value={searchValue}
                        onChange={e => setSearchValue(e.target.value)}
                        onSearch={handleSearch}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Button icon={<ReloadOutlined />} onClick={() => fetchEntries(pagination.current, pagination.pageSize)}>
                        刷新
                    </Button>
                    <Divider type="vertical" />
                    <Tooltip title="临时封禁（限速触发）">
                        <Button icon={<LockOutlined />} onClick={handleBlock} disabled={!selectedCluster}>
                            手动封禁
                        </Button>
                    </Tooltip>
                    <Tooltip title="解除临时封禁">
                        <Button icon={<UnlockOutlined />} onClick={handleUnblock} disabled={!selectedCluster}>
                            解除封禁
                        </Button>
                    </Tooltip>
                    <Divider type="vertical" />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!selectedCluster}>
                        添加{activeTab === 'blacklist' ? '黑名单' : '白名单'}
                    </Button>
                </Space>
            }
        >
            {!selectedCluster && (
                <Alert message="请先选择一个邮件集群" type="warning" showIcon style={{ marginBottom: 16 }} />
            )}

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane
                    tab={<span><StopOutlined style={{ color: '#ff4d4f' }} /> 黑名单</span>}
                    key="blacklist"
                >
                    <Table
                        columns={columns}
                        dataSource={entries}
                        rowKey="id"
                        loading={loading}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: <Empty description={selectedCluster ? '暂无条目，点击右上角添加' : '请先选择集群'} /> }}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 条`,
                            onChange: (page, pageSize) => fetchEntries(page, pageSize),
                        }}
                    />
                </TabPane>
                <TabPane
                    tab={<span><CheckCircleOutlined style={{ color: '#52c41a' }} /> 白名单</span>}
                    key="whitelist"
                >
                    <Table
                        columns={columns}
                        dataSource={entries}
                        rowKey="id"
                        loading={loading}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: <Empty description={selectedCluster ? '暂无条目，点击右上角添加' : '请先选择集群'} /> }}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 条`,
                            onChange: (page, pageSize) => fetchEntries(page, pageSize),
                        }}
                    />
                </TabPane>
            </Tabs>

            {/* 添加黑白名单 Modal */}
            <Modal
                title={`添加${activeTab === 'blacklist' ? '黑名单' : '白名单'}`}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={550}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="entry_type" label="类型" rules={[{ required: true }]}>
                        <Select options={entryTypeOptions} />
                    </Form.Item>
                    <Form.Item
                        name="value"
                        label="值"
                        rules={[{ required: true, message: '请输入值' }]}
                        extra="支持 IP 地址、邮箱、域名等"
                    >
                        <Input placeholder="如：192.168.1.1 或 spam@example.com 或 example.com" />
                    </Form.Item>
                    <Form.Item name="reason" label="原因">
                        <TextArea rows={2} placeholder="添加此条目的原因" />
                    </Form.Item>
                    <Space style={{ width: '100%' }}>
                        <Form.Item name="scope" label="范围" rules={[{ required: true }]} style={{ width: 120 }}>
                            <Select options={scopeOptions} />
                        </Form.Item>
                        <Form.Item name="scope_value" label="范围值" style={{ flex: 1 }} extra="如果范围是域名或用户，填写具体值">
                            <Input placeholder="如：example.com" />
                        </Form.Item>
                    </Space>
                    <Form.Item label="过期时间">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Select
                                value={expiryMode}
                                onChange={setExpiryMode}
                                style={{ width: '100%' }}
                            >
                                {expiryOptions.map(opt => (
                                    <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                                ))}
                            </Select>
                            {expiryMode === -1 && (
                                <Form.Item name="expires_at" noStyle>
                                    <DatePicker
                                        showTime
                                        style={{ width: '100%' }}
                                        placeholder="选择过期时间"
                                        disabledDate={(current) => current && current < dayjs().startOf('day')}
                                    />
                                </Form.Item>
                            )}
                        </Space>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>添加</Button>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 手动封禁 Modal */}
            <Modal
                title="手动封禁"
                open={blockModalVisible}
                onCancel={() => setBlockModalVisible(false)}
                footer={null}
                width={500}
            >
                <Alert
                    message="临时封禁"
                    description="此功能用于手动触发限速封禁，封禁数据存储在 Redis 中，会在到期后自动解除。如需永久封禁，请使用黑名单功能。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form form={blockForm} layout="vertical" onFinish={handleBlockSubmit}>
                    <Form.Item name="type" label="封禁类型" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="ip">IP 地址</Select.Option>
                            <Select.Option value="sender">发件人</Select.Option>
                            <Select.Option value="domain">域名</Select.Option>
                            <Select.Option value="recipient">收件人</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="key"
                        label="封禁目标"
                        rules={[{ required: true, message: '请输入封禁目标' }]}
                    >
                        <Input placeholder="如：192.168.1.1 或 spam@example.com" />
                    </Form.Item>
                    <Form.Item name="duration" label="封禁时长 (秒)" rules={[{ required: true }]}>
                        <InputNumber min={60} max={2592000} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="reason" label="封禁原因">
                        <Input placeholder="可选" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" danger htmlType="submit" icon={<LockOutlined />} loading={submitting}>
                                确认封禁
                            </Button>
                            <Button onClick={() => setBlockModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 解除封禁 Modal */}
            <Modal
                title="解除封禁"
                open={unblockModalVisible}
                onCancel={() => setUnblockModalVisible(false)}
                footer={null}
                width={500}
            >
                <Alert
                    message="解除临时封禁"
                    description="此功能用于解除限速触发的临时封禁（Redis 中的封禁记录）。如需解除黑名单，请在黑名单列表中删除对应条目。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form form={unblockForm} layout="vertical" onFinish={handleUnblockSubmit}>
                    <Form.Item name="type" label="类型" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="ip">IP 地址</Select.Option>
                            <Select.Option value="sender">发件人</Select.Option>
                            <Select.Option value="domain">域名</Select.Option>
                            <Select.Option value="recipient">收件人</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="key"
                        label="解封目标"
                        rules={[{ required: true, message: '请输入解封目标' }]}
                    >
                        <Input placeholder="如：192.168.1.1 或 spam@example.com" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" icon={<UnlockOutlined />} loading={submitting}>
                                确认解封
                            </Button>
                            <Button onClick={() => setUnblockModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Blacklist;
